// The alert calendar of tenant-db-schema docs/alert-calendar.md: a list of windows of local weekdays and
// hours. The stored list is the only state. The editor's rows are derived from it on every render, and
// an edit rewrites only the windows of the row it edits, so untouched windows keep their stored bytes.

/** 0 is Sunday and 6 is Saturday. Not DayOfWeek, which counts from 1. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6
export type CalendarWindow = { weekdays?: Weekday[]; hours?: number[] }
export type Calendar = CalendarWindow[]
/** A day check judges whole days, so its windows carry no hours. */
export type DayCalendarWindow = { weekdays: Weekday[] }
export type Grain = 'day' | 'hour'

export type RowHours =
  | { kind: 'all' }
  /** `from` 0..23, `to` 1..24. `to <= from` means until `to` on the next day. */
  | { kind: 'range'; from: number; to: number }
  /** Stored hours that are not one run. Read-only in the editor. */
  | { kind: 'scattered'; hours: number[] }
export type EditableRowHours = Exclude<RowHours, { kind: 'scattered' }>

/** `windows` indexes the stored list. A night is [evening, morning]. */
export type CalendarRow = {
  windows: number[]
  days: Weekday[]
  hours: RowHours
}

export const ALL_WEEKDAYS: readonly Weekday[] = [0, 1, 2, 3, 4, 5, 6]
export const WEEKDAY_SHORT = ['א׳', 'ב׳', 'ג׳', 'ד׳', 'ה׳', 'ו׳', 'ש׳'] as const
export const WEEKDAY_LONG = [
  'ראשון',
  'שני',
  'שלישי',
  'רביעי',
  'חמישי',
  'שישי',
  'שבת',
] as const

/** The schema's rules for a stored calendar. Null when it breaks one. */
export function readCalendar(raw: unknown): Calendar | null {
  if (!Array.isArray(raw) || raw.length === 0) return null
  const cal: Calendar = []
  for (const item of raw) {
    if (typeof item !== 'object' || item === null || Array.isArray(item)) {
      return null
    }
    const keys = Object.keys(item)
    if (keys.length === 0) return null
    const window: CalendarWindow = {}
    for (const key of keys) {
      const max = key === 'weekdays' ? 6 : key === 'hours' ? 23 : null
      const values = (item as Record<string, unknown>)[key]
      if (max === null || !isSet(values, max)) return null
      if (key === 'weekdays') window.weekdays = [...values] as Weekday[]
      else window.hours = [...values]
    }
    cal.push(window)
  }
  return cal
}

function isSet(values: unknown, max: number): values is number[] {
  return (
    Array.isArray(values) &&
    values.length > 0 &&
    new Set(values).size === values.length &&
    values.every(v => Number.isInteger(v) && v >= 0 && v <= max)
  )
}

export function cloneCalendar<W extends CalendarWindow>(cal: W[]): W[] {
  return cal.map(
    w =>
      Object.fromEntries(
        Object.entries(w).map(([k, v]) => [k, [...(v as number[])]])
      ) as W
  )
}

function daysOf(w: CalendarWindow): Weekday[] {
  return w.weekdays === undefined
    ? [...ALL_WEEKDAYS]
    : [...w.weekdays].sort((a, b) => a - b)
}

function hoursOf(w: CalendarWindow): RowHours {
  if (w.hours === undefined) return { kind: 'all' }
  const hours = [...new Set(w.hours)].sort((a, b) => a - b)
  const first = hours[0]
  const last = hours[hours.length - 1]
  return hours.length > 0 && last - first + 1 === hours.length
    ? { kind: 'range', from: first, to: last + 1 }
    : { kind: 'scattered', hours }
}

const sameDays = (a: Weekday[], b: Weekday[]) =>
  a.length === b.length && a.every((d, i) => d === b[i])

const nextDays = (days: Weekday[]) =>
  days.map(d => ((d + 1) % 7) as Weekday).sort((a, b) => a - b)

/**
 * An evening (one run ending at 23, starting after 0) pairs with the first unclaimed morning (one run
 * from 0, ending by the evening's start) on the next weekdays, wherever it sits in the list.
 */
export function toRows(cal: Calendar): CalendarRow[] {
  const shapes = cal.map(w => ({ days: daysOf(w), hours: hoursOf(w) }))
  const claimed = new Set<number>()
  const rows: CalendarRow[] = []
  shapes.forEach((evening, i) => {
    const eh = evening.hours
    if (eh.kind !== 'range' || eh.to !== 24 || eh.from === 0) return
    const morningDays = nextDays(evening.days)
    const j = shapes.findIndex(
      (m, k) =>
        k !== i &&
        !claimed.has(k) &&
        m.hours.kind === 'range' &&
        m.hours.from === 0 &&
        m.hours.to <= eh.from &&
        sameDays(m.days, morningDays)
    )
    const mh = shapes[j]?.hours
    if (j === -1 || mh?.kind !== 'range') return
    claimed.add(i).add(j)
    rows.push({
      windows: [i, j],
      days: evening.days,
      hours: { kind: 'range', from: eh.from, to: mh.to },
    })
  })
  shapes.forEach((shape, i) => {
    if (!claimed.has(i)) rows.push({ windows: [i], ...shape })
  })
  return rows.sort((a, b) => Math.min(...a.windows) - Math.min(...b.windows))
}

function normalHours(hours: EditableRowHours): EditableRowHours {
  return hours.kind === 'range' && hours.from === 0 && hours.to === 24
    ? { kind: 'all' }
    : hours
}

function sameHours(a: EditableRowHours, b: EditableRowHours): boolean {
  if (a.kind === 'all' || b.kind === 'all') return a.kind === b.kind
  return a.from === b.from && a.to === b.to
}

function encodeRow(days: Weekday[], hours: EditableRowHours): Calendar {
  const sorted = [...new Set(days)].sort((a, b) => a - b)
  const everyDay = sorted.length === 7
  const window = (d: Weekday[], h?: number[]): CalendarWindow => ({
    ...(h === undefined || !everyDay ? { weekdays: d } : {}),
    ...(h !== undefined ? { hours: h } : {}),
  })
  if (hours.kind === 'all') return [window(sorted)]
  const run = (from: number, to: number) =>
    Array.from({ length: to - from }, (_, i) => from + i)
  if (hours.from < hours.to) return [window(sorted, run(hours.from, hours.to))]
  return [
    window(sorted, run(hours.from, 24)),
    window(nextDays(sorted), run(0, hours.to)),
  ]
}

/**
 * Replaces the windows of one row in place and keeps every other window object. An edit that leaves the
 * row's days and hours as they were returns `cal` itself, so touching a control and undoing it keeps the
 * stored bytes.
 */
export function setRow(
  cal: Calendar,
  row: number,
  days: Weekday[],
  hours: EditableRowHours,
  grain: Grain
): Calendar {
  const current = toRows(cal)[row]
  if (!current) throw new Error(`setRow: no row ${row}`)
  if (current.hours.kind === 'scattered') {
    throw new Error(`setRow: row ${row} has scattered hours and is read-only`)
  }
  const nextHours = normalHours(grain === 'day' ? { kind: 'all' } : hours)
  const sortedDays = [...new Set(days)].sort((a, b) => a - b)
  const was = normalHours(current.hours)
  if (sameDays(sortedDays, current.days) && sameHours(nextHours, was)) {
    return cal
  }
  const [first, second] = current.windows
  const [head, tail] = encodeRow(sortedDays, nextHours)
  const out: Array<CalendarWindow | null> = [...cal]
  out[first] = head
  if (second !== undefined) out[second] = tail ?? null
  else if (tail) out.splice(first + 1, 0, tail)
  return out.filter((w): w is CalendarWindow => w !== null)
}

export function addRow(cal: Calendar | undefined): Calendar {
  return [...(cal ?? []), { weekdays: [...ALL_WEEKDAYS] }]
}

/** Undefined when the last row goes: no rows is no calendar. */
export function removeRow(cal: Calendar, row: number): Calendar | undefined {
  const drop = new Set(toRows(cal)[row]?.windows ?? [])
  const out = cal.filter((_, i) => !drop.has(i))
  return out.length > 0 ? out : undefined
}

/** For a switch to a day check: no window keeps hours, and windows with equal days merge. */
export function stripHours(cal: Calendar): DayCalendarWindow[] {
  if (cal.every(w => w.hours === undefined)) return cal as DayCalendarWindow[]
  const out: DayCalendarWindow[] = []
  const seen: Weekday[][] = []
  for (const w of cal) {
    const days = daysOf(w)
    if (seen.some(s => sameDays(s, days))) continue
    seen.push(days)
    out.push(
      w.hours === undefined
        ? (w as DayCalendarWindow)
        : { weekdays: w.weekdays ? [...w.weekdays] : days }
    )
  }
  return out
}

const LRI = '⁦'
const PDI = '⁩'
const clock = (h: number) => `${String(h).padStart(2, '0')}:00`

export function daysText(days: Weekday[]): string {
  if (days.length === 0) return 'לא נבחרו ימים'
  if (days.length === 7) return 'כל יום'
  const runs: Weekday[][] = []
  for (const d of [...days].sort((a, b) => a - b)) {
    const last = runs[runs.length - 1]
    if (last && last[last.length - 1] === d - 1) last.push(d)
    else runs.push([d])
  }
  return runs
    .map(r =>
      r.length === 1
        ? WEEKDAY_SHORT[r[0]]
        : `${WEEKDAY_SHORT[r[0]]}–${WEEKDAY_SHORT[r[r.length - 1]]}`
    )
    .join(', ')
}

/** Clock text in an LTR isolate, so "22:00–06:00" reads left to right inside a Hebrew line. Empty for all hours. */
export function hoursText(hours: RowHours): string {
  if (hours.kind === 'all') return ''
  if (hours.kind === 'range') {
    return `${LRI}${clock(hours.from)}–${clock(hours.to)}${PDI}`
  }
  const runs: number[][] = []
  for (const h of hours.hours) {
    const last = runs[runs.length - 1]
    if (last && last[last.length - 1] === h - 1) last.push(h)
    else runs.push([h])
  }
  const text = runs
    .map(r => `${clock(r[0])}–${clock(r[r.length - 1] + 1)}`)
    .join(', ')
  return `${LRI}${text}${PDI}`
}

export function rowText(row: CalendarRow): string {
  const hours = hoursText(row.hours)
  return hours === '' ? daysText(row.days) : `${daysText(row.days)} ${hours}`
}

/** One Hebrew line for the node card, the header chip and aria labels. */
export function describeCalendar(cal: Calendar): string {
  return toRows(cal).map(rowText).join(' · ')
}

/** What blocks a save of a calendar being edited, in Hebrew, by row number. */
export function calendarDraftProblems(cal: Calendar, grain: Grain): string[] {
  if (cal.length === 0) return ['אין בו אף שורה']
  const problems: string[] = []
  toRows(cal).forEach((row, i) => {
    if (row.days.length === 0) {
      problems.push(`בשורה ${i + 1} לא נבחר אף יום`)
    }
    if (grain === 'day' && row.windows.some(w => cal[w].hours !== undefined)) {
      problems.push(`בשורה ${i + 1} יש שעות, ובדיקה יומית לא יכולה להשתמש בהן`)
    }
  })
  return problems
}
